import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

interface LocationState {
  isLocationEnabled: boolean;
  isLocationRequired: boolean;
  coordinates: GeolocationCoordinates | null;
  latitude: number | null;
  longitude: number | null;
  error: string | null;
  lastChecked: Date | null;
  isChecking: boolean;
  showModal: boolean;
  isWatchingLocation: boolean;
  locationHistory: Array<{
    latitude: number;
    longitude: number;
    timestamp: Date;
  }>;
  distanceMoved: number; // Distance moved in meters since last check
}

interface LocationContextType extends LocationState {
  requestLocation: () => Promise<void>;
  checkLocationStatus: () => Promise<void>;
  dismissLocationRequest: () => void;
  setLocationRequired: (required: boolean) => void;
  startWatchingLocation: () => void;
  stopWatchingLocation: () => void;
  clearLocationHistory: () => void;
  setManualLocation: (lat: number, lng: number) => void;
  onLocationChange?: (coords: {
    latitude: number;
    longitude: number;
  }) => void;
}

const LocationContext = createContext<
  LocationContextType | undefined
>(undefined);

const LOCATION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
const LOCATION_STORAGE_KEY = 'location_permission_status';

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
}) => {
  const [locationState, setLocationState] = useState<LocationState>({
    isLocationEnabled: false,
    isLocationRequired: true,
    coordinates: null,
    latitude: null,
    longitude: null,
    error: null,
    lastChecked: null,
    isChecking: false,
    showModal: false,
    isWatchingLocation: false,
    locationHistory: [],
    distanceMoved: 0,
  });

  const [watchId, setWatchId] = useState<number | null>(null);

  // Check if geolocation API is supported
  const isGeolocationSupported = 'geolocation' in navigator;

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) *
        Math.cos(φ2) *
        Math.sin(Δλ / 2) *
        Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Start watching location changes in real-time
  const startWatchingLocation = useCallback(() => {
    if (!isGeolocationSupported || watchId !== null) return;

    const id = navigator.geolocation.watchPosition(
      (position) => {
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;

        setLocationState((prev) => {
          // Calculate distance moved if we have previous coordinates
          let distance = 0;
          if (prev.latitude && prev.longitude) {
            distance = calculateDistance(
              prev.latitude,
              prev.longitude,
              newLat,
              newLng,
            );
          }

          // Add to history
          const newHistory = [
            ...prev.locationHistory,
            {
              latitude: newLat,
              longitude: newLng,
              timestamp: new Date(),
            },
          ].slice(-20); // Keep last 20 positions

          return {
            ...prev,
            coordinates: position.coords,
            latitude: newLat,
            longitude: newLng,
            isLocationEnabled: true,
            isWatchingLocation: true,
            locationHistory: newHistory,
            distanceMoved: distance,
            lastChecked: new Date(),
            showModal: false,
          };
        });

        // Log to backend for real-time tracking
        logLocationToBackend(position.coords);
      },
      (error) => {
        console.error('Watch position error:', error);
        setLocationState((prev) => ({
          ...prev,
          isWatchingLocation: false,
        }));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 5000,
      },
    );

    setWatchId(id);
  }, [isGeolocationSupported, watchId]);

  // Stop watching location changes
  const stopWatchingLocation = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
      setLocationState((prev) => ({
        ...prev,
        isWatchingLocation: false,
      }));
    }
  }, [watchId]);

  // Clear location history
  const clearLocationHistory = useCallback(() => {
    setLocationState((prev) => ({
      ...prev,
      locationHistory: [],
      distanceMoved: 0,
    }));
  }, []);

  // Set location manually (from map picker)
  const setManualLocation = useCallback(
    (lat: number, lng: number) => {
      setLocationState((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
        coordinates: {
          latitude: lat,
          longitude: lng,
          accuracy: 0,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
        } as GeolocationCoordinates,
        isLocationEnabled: true,
        error: null,
        showModal: false,
        lastChecked: new Date(),
      }));

      // Log manual location to backend
      const manualCoords = {
        latitude: lat,
        longitude: lng,
        accuracy: 0,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
      } as GeolocationCoordinates;

      logLocationToBackend(manualCoords);

      localStorage.setItem('location_manual', 'true');
      localStorage.setItem(
        'location_coords',
        JSON.stringify({ lat, lng }),
      );
    },
    [],
  );

  // Request location permission and get coordinates
  const requestLocation = useCallback(async (): Promise<void> => {
    if (!isGeolocationSupported) {
      setLocationState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
        isLocationEnabled: false,
      }));
      return;
    }

    setLocationState((prev) => ({
      ...prev,
      isChecking: true,
      error: null,
    }));

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        },
      );

      setLocationState((prev) => ({
        ...prev,
        isLocationEnabled: true,
        coordinates: position.coords,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        error: null,
        lastChecked: new Date(),
        isChecking: false,
        showModal: false, // Hide modal on success
      }));

      // Store success status
      localStorage.setItem(LOCATION_STORAGE_KEY, 'granted');

      // Send location to backend for logging/compliance
      await logLocationToBackend(position.coords);
    } catch (error: any) {
      let errorMessage = 'Failed to get location';

      if (error.code === error.PERMISSION_DENIED) {
        errorMessage =
          'Location permission denied. Please enable location access in your browser settings.';
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        errorMessage = 'Location information unavailable.';
      } else if (error.code === error.TIMEOUT) {
        errorMessage = 'Location request timed out.';
      }

      setLocationState((prev) => ({
        ...prev,
        isLocationEnabled: false,
        coordinates: null,
        latitude: null,
        longitude: null,
        error: errorMessage,
        lastChecked: new Date(),
        isChecking: false,
        showModal: true, // Show modal when location fails
      }));

      localStorage.setItem(LOCATION_STORAGE_KEY, 'denied');
    }
  }, [isGeolocationSupported]);

  // Check current location status without requesting
  const checkLocationStatus = useCallback(async (): Promise<void> => {
    if (!isGeolocationSupported) return;

    // Check if permissions API is available
    if ('permissions' in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        });

        if (permissionStatus.state === 'granted') {
          await requestLocation();
        } else if (permissionStatus.state === 'denied') {
          setLocationState((prev) => ({
            ...prev,
            isLocationEnabled: false,
            coordinates: null,
            latitude: null,
            longitude: null,
            error:
              'Location access is blocked. Please enable it in browser settings.',
            lastChecked: new Date(),
            showModal: true, // Show modal only when denied
          }));
        } else {
          // State is 'prompt' - user hasn't decided yet
          setLocationState((prev) => ({
            ...prev,
            isLocationEnabled: false,
            lastChecked: new Date(),
            showModal: !prev.isLocationEnabled, // Show modal only if not enabled
          }));
        }
      } catch (error) {
        // Permissions API might not support geolocation query in all browsers
        // Fall back to trying to get location
        await requestLocation();
      }
    } else {
      // Permissions API not available, try to get location
      await requestLocation();
    }
  }, [isGeolocationSupported, requestLocation]);

  // Dismiss location request temporarily (user can still use app with limitations)
  const dismissLocationRequest = useCallback(() => {
    setLocationState((prev) => ({
      ...prev,
      error: null,
      showModal: false,
    }));
  }, []);

  // Set whether location is required
  const setLocationRequired = useCallback((required: boolean) => {
    setLocationState((prev) => ({
      ...prev,
      isLocationRequired: required,
    }));
  }, []);

  // Log location to backend
  const logLocationToBackend = async (
    coords: GeolocationCoordinates,
  ) => {
    try {
      // Replace with your actual API endpoint
      const userToken = localStorage.getItem('userToken');
      if (!userToken) return;

      await fetch('/api/user/location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Failed to log location:', error);
      // Don't block user if logging fails
    }
  };

  // Initial location check on mount
  useEffect(() => {
    const userToken = localStorage.getItem('userToken');
    if (userToken && locationState.isLocationRequired) {
      // Show modal on first check if location is required
      setLocationState((prev) => ({ ...prev, showModal: true }));
      checkLocationStatus();
    }
  }, []);

  // Periodic location checks every 5 minutes
  useEffect(() => {
    const userToken = localStorage.getItem('userToken');
    if (!userToken || !locationState.isLocationRequired) return;

    const intervalId = setInterval(() => {
      checkLocationStatus();
    }, LOCATION_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [checkLocationStatus, locationState.isLocationRequired]);

  // Listen for permission changes
  useEffect(() => {
    if (!isGeolocationSupported || !('permissions' in navigator))
      return;

    const checkPermissionChange = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        });

        permissionStatus.addEventListener('change', () => {
          checkLocationStatus();
        });
      } catch (error) {
        // Permissions API not fully supported
      }
    };

    checkPermissionChange();
  }, [isGeolocationSupported, checkLocationStatus]);

  // Cleanup: Stop watching location on unmount
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  const value: LocationContextType = {
    ...locationState,
    requestLocation,
    checkLocationStatus,
    dismissLocationRequest,
    setLocationRequired,
    startWatchingLocation,
    stopWatchingLocation,
    clearLocationHistory,
    setManualLocation,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use location context
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error(
      'useLocation must be used within a LocationProvider',
    );
  }
  return context;
};
